// ============================================================
//  IocInfoPopover — on-demand enrichment popover for a domain / IP
// ============================================================
//
// Renders a small (i) icon next to an entity (sender domain or originating
// IP). Hovering, focusing, or clicking the icon opens a floating information
// box that lazy-loads enrichment from /api/enrichment/<type>/<value>:
//   • WHOIS / registration + domain age   (domain)
//   • Network / ASN / geo                  (ip)
//   • Live DNS records                     (domain)
//   • Reverse DNS                          (ip)
//   • Passive DNS history
//   • Reputation snapshot
//
// The box is rendered through a portal so it is never clipped by the
// surrounding summary card, and it stays open while the pointer is over
// either the icon or the box itself.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaSpinner, FaExternalLinkAlt, FaExclamationTriangle } from "react-icons/fa";
import { FiInfo } from "react-icons/fi";

/* ---------------------- helpers ---------------------- */

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function Row({ label, children }) {
  return (
    <div className="ioc-pop-row">
      <span className="ioc-pop-row-label">{label}</span>
      <span className="ioc-pop-row-value">{children}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="ioc-pop-section">
      <div className="ioc-pop-section-title">{title}</div>
      {children}
    </div>
  );
}

function ChipList({ items, max = 6 }) {
  if (!items || items.length === 0) return <span className="ioc-pop-muted">None found</span>;
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  return (
    <span className="ioc-pop-chips">
      {shown.map((it, i) => (
        <span key={i} className="ioc-pop-chip">
          {it}
        </span>
      ))}
      {extra > 0 && <span className="ioc-pop-chip ioc-pop-chip-more">+{extra} more</span>}
    </span>
  );
}

/* ---------------------- body renderers ---------------------- */

function DomainBody({ data }) {
  const { whois = {}, age, dns = {}, passiveDns = [], reputation, vtConfigured } = data;
  return (
    <>
      {age && (
        <Section title="Domain Age">
          <div className="ioc-pop-age">
            <span className={`ioc-pop-age-label tier-${age.tier || "low"}`}>{age.label}</span>
            {age.tier === "high" && (
              <span className="ioc-pop-badge ioc-pop-badge-bad">Newly registered</span>
            )}
            {age.tier === "medium" && (
              <span className="ioc-pop-badge ioc-pop-badge-warn">Recently registered</span>
            )}
          </div>
          <Row label="Registered">{fmtDate(age.createdDate)}</Row>
        </Section>
      )}

      <Section title="WHOIS / Registration">
        {whois.isSubdomain && whois.domain && (
          <div className="ioc-pop-subnote">
            Registration shown for the parent domain{" "}
            <span className="ioc-pop-mono">{whois.domain}</span>
          </div>
        )}
        {whois.available ? (
          <>
            <Row label="Registrar">{whois.registrar || "—"}</Row>
            {!age && <Row label="Created">{fmtDate(whois.createdDate)}</Row>}
            <Row label="Updated">{fmtDate(whois.updatedDate)}</Row>
            <Row label="Expires">{fmtDate(whois.expiresDate)}</Row>
            {whois.status?.length > 0 && (
              <Row label="Status">
                <ChipList items={whois.status} max={3} />
              </Row>
            )}
            <Row label="Name servers">
              <ChipList items={whois.nameServers} max={4} />
            </Row>
          </>
        ) : (
          <span className="ioc-pop-muted">No registration data available.</span>
        )}
      </Section>

      <Section title="DNS Records">
        <Row label="A">
          <ChipList items={dns.A} max={3} />
        </Row>
        <Row label="MX">
          <ChipList items={dns.MX} max={2} />
        </Row>
        <Row label="NS">
          <ChipList items={dns.NS} max={3} />
        </Row>
        <Row label="TXT">
          <ChipList items={dns.TXT} max={2} />
        </Row>
      </Section>

      <Section title="Passive DNS History">
        {passiveDns.length > 0 ? (
          <div className="ioc-pop-pdns">
            {passiveDns.slice(0, 5).map((r, i) => (
              <div key={i} className="ioc-pop-pdns-row">
                <span className="ioc-pop-pdns-ip">
                  <span className="ioc-pop-mono">{r.ip}</span>
                  <a
                    className="ioc-pop-ip-link"
                    href={`https://www.virustotal.com/gui/ip-address/${encodeURIComponent(r.ip)}/details`}
                    target="_blank"
                    rel="noreferrer"
                    title={`Look up ${r.ip} on VirusTotal`}
                  >
                    <FaExternalLinkAlt />
                  </a>
                </span>
                <span className="ioc-pop-muted">{fmtDate(r.date)}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="ioc-pop-muted">
            {vtConfigured ? "No historical resolutions found." : "Add a VirusTotal key to see history."}
          </span>
        )}
      </Section>

      <Reputation reputation={reputation} vtConfigured={vtConfigured} />
    </>
  );
}

function IpBody({ data }) {
  const { network = {}, reverseDns = [], passiveDns = [], reputation, vtConfigured, private: isPrivate, message } =
    data;

  if (isPrivate) {
    return <p className="ioc-pop-muted ioc-pop-private">{message}</p>;
  }

  const pdnsHosts = passiveDns.map((p) => p.hostname).filter(Boolean);

  return (
    <>
      <Section title="Network">
        <Row label="ASN">{network.asn || "—"}</Row>
        <Row label="ISP / Org">{network.isp || network.name || "—"}</Row>
        <Row label="CIDR">{network.cidr ? <span className="ioc-pop-mono">{network.cidr}</span> : "—"}</Row>
        {network.type && <Row label="Type">{network.type}</Row>}
        <Row label="Location">
          {[network.city, network.region, network.country].filter(Boolean).join(", ") || "—"}
        </Row>
        {network.registeredOn && <Row label="Registered">{fmtDate(network.registeredOn)}</Row>}
      </Section>

      <Section title="Reverse DNS">
        <ChipList items={reverseDns} max={4} />
      </Section>

      <Section title="Passive DNS History">
        {pdnsHosts.length > 0 ? (
          <ChipList items={pdnsHosts} max={8} />
        ) : (
          <span className="ioc-pop-muted">
            {vtConfigured ? "No historical hostnames found." : "Add a VirusTotal key to see history."}
          </span>
        )}
      </Section>

      <Reputation reputation={reputation} vtConfigured={vtConfigured} />
    </>
  );
}

function Reputation({ reputation, vtConfigured }) {
  if (!reputation) {
    if (!vtConfigured) return null;
    return (
      <Section title="Reputation">
        <span className="ioc-pop-muted">No reputation data.</span>
      </Section>
    );
  }
  if (reputation.rateLimited) {
    return (
      <Section title="Reputation (VirusTotal)">
        <span className="ioc-pop-ratelimited">
          Rate limit reached — try again shortly.
        </span>
      </Section>
    );
  }
  const bad = (reputation.positives || 0) > 0;
  return (
    <Section title="Reputation (VirusTotal)">
      <div className="ioc-pop-rep">
        <span className={`ioc-pop-badge ${bad ? "ioc-pop-badge-bad" : "ioc-pop-badge-good"}`}>
          {reputation.positives != null && reputation.total != null
            ? `${reputation.positives}/${reputation.total} detections`
            : reputation.verdict}
        </span>
        {reputation.link && (
          <a className="ioc-pop-link" href={reputation.link} target="_blank" rel="noreferrer">
            View on VirusTotal <FaExternalLinkAlt />
          </a>
        )}
      </div>
    </Section>
  );
}

/* ---------------------- main component ---------------------- */

const OPEN_DELAY = 120;
const CLOSE_DELAY = 180;

export default function IocInfoPopover({ type, value, label }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, placeAbove: false });

  const iconRef = useRef(null);
  const fetchedRef = useRef(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const entity = label || value;

  const load = useCallback(async () => {
    if (fetchedRef.current || !value) return;
    fetchedRef.current = true;
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`/api/enrichment/${type}/${encodeURIComponent(value)}`);
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.ok) {
        throw new Error(json.error || `Lookup failed (${resp.status})`);
      }
      setData(json);
    } catch (e) {
      fetchedRef.current = false; // allow retry on next open
      setError(e.message || "Could not load details.");
    } finally {
      setLoading(false);
    }
  }, [type, value]);

  const position = useCallback(() => {
    const el = iconRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const POP_W = 340;
    const estH = 360;
    const spaceBelow = window.innerHeight - r.bottom;
    const placeAbove = spaceBelow < estH && r.top > spaceBelow;

    let left = r.left + r.width / 2 - POP_W / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - POP_W - 12));

    setCoords({
      top: placeAbove ? r.top - 8 : r.bottom + 8,
      left,
      placeAbove,
    });
  }, []);

  const doOpen = useCallback(() => {
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => {
      position();
      setOpen(true);
      load();
    }, OPEN_DELAY);
  }, [position, load]);

  const doClose = useCallback(() => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  }, []);

  const toggle = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        position();
        load();
      }
      return next;
    });
  }, [position, load]);

  // Reposition / dismiss on scroll, resize, escape.
  useEffect(() => {
    if (!open) return undefined;
    const onScrollResize = () => position();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, position]);

  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    []
  );

  return (
    <>
      <button
        type="button"
        ref={iconRef}
        className="ioc-info-trigger"
        aria-label={`Show details for ${entity}`}
        aria-expanded={open}
        onMouseEnter={doOpen}
        onMouseLeave={doClose}
        onFocus={doOpen}
        onBlur={doClose}
        onClick={toggle}
      >
        <FiInfo />
      </button>

      {open &&
        createPortal(
          <div
            className={`ioc-info-popover ${coords.placeAbove ? "place-above" : ""}`}
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={() => clearTimeout(closeTimer.current)}
            onMouseLeave={doClose}
            role="dialog"
            aria-label={`Details for ${entity}`}
          >
            <div className="ioc-pop-header">
              <span className="ioc-pop-kind">{type === "ip" ? "IP Address" : "Domain"}</span>
              <span className="ioc-pop-entity" title={entity}>
                {entity}
              </span>
            </div>

            <div className="ioc-pop-content">
              {loading && (
                <div className="ioc-pop-status">
                  <FaSpinner className="ioc-pop-spin" /> Gathering intelligence…
                </div>
              )}

              {!loading && error && (
                <div className="ioc-pop-status ioc-pop-status-error">
                  <FaExclamationTriangle /> {error}
                </div>
              )}

              {!loading && !error && data && type === "domain" && <DomainBody data={data} />}
              {!loading && !error && data && type === "ip" && <IpBody data={data} />}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
